import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

// ⭐ Componente de alerta compartido. Antes existían 9 copias casi
// idénticas de esto repartidas por pos/ (Caja, Categorías, Configuración,
// Empleados, Menú Web, Mesas, Pedidos Pendientes, Productos, Reportes),
// que ya habían empezado a divergir en detalles menores (z-index distinto
// en cada pantalla, algunas con emoji y otras sin, una sola con botón de
// Confirmar/Cancelar) — el tipo de duplicación que con el tiempo termina
// generando 9 comportamientos ligeramente distintos de "la misma" alerta.
//
// Esta unificación NO cambia cómo se ve cada pantalla: cada detalle que
// variaba de un archivo a otro (z-index, variante compacta, botón de
// confirmar) ahora es un prop explícito en vez de una copia de código
// ligeramente distinta. Los z-index se mapean a clases Tailwind completas
// y estáticas (no `z-[${zIndex}]` interpolado) porque el compilador de
// Tailwind solo genera CSS para clases que puede ver como texto literal
// completo en el código — una clase armada en runtime no generaría estilo.
const Z_INDEX_CLASSES = {
  50: 'z-50',
  60: 'z-[60]',
  70: 'z-[70]',
  80: 'z-[80]',
};

const FULL_CONFIGS = {
  success: {
    bgColor: 'bg-gradient-to-r from-green-50 to-emerald-50',
    borderColor: 'border-green-500',
    iconColor: 'text-green-600',
    titleColor: 'text-green-800',
    Icon: CheckCircle,
    emoji: '✅',
  },
  error: {
    bgColor: 'bg-gradient-to-r from-red-50 to-rose-50',
    borderColor: 'border-red-500',
    iconColor: 'text-red-600',
    titleColor: 'text-red-800',
    Icon: XCircle,
    emoji: '❌',
  },
  warning: {
    bgColor: 'bg-gradient-to-r from-yellow-50 to-orange-50',
    borderColor: 'border-orange-500',
    iconColor: 'text-orange-600',
    titleColor: 'text-orange-800',
    Icon: AlertTriangle,
    emoji: '⚠️',
  },
  confirm: {
    bgColor: 'bg-gradient-to-r from-blue-50 to-indigo-50',
    borderColor: 'border-blue-500',
    iconColor: 'text-blue-600',
    titleColor: 'text-blue-800',
    Icon: AlertTriangle,
    emoji: '❓',
  },
};

const COMPACT_CONFIGS = {
  success: { bg: 'bg-gradient-to-r from-green-50 to-emerald-50', border: 'border-green-500', iconColor: 'text-green-600', titleColor: 'text-green-800', Icon: CheckCircle },
  error: { bg: 'bg-gradient-to-r from-red-50 to-rose-50', border: 'border-red-500', iconColor: 'text-red-600', titleColor: 'text-red-800', Icon: XCircle },
  warning: { bg: 'bg-gradient-to-r from-yellow-50 to-orange-50', border: 'border-orange-500', iconColor: 'text-orange-600', titleColor: 'text-orange-800', Icon: AlertTriangle },
};

/**
 * @param {'success'|'error'|'warning'|'confirm'} type
 * @param {string} title
 * @param {string} message
 * @param {() => void} onClose
 * @param {() => void} [onConfirm] - si se pasa, muestra Cancelar/Confirmar en vez de Entendido (solo variant="full")
 * @param {boolean} [compact] - variante chica sin encabezado/emoji (Caja, Categorías, Menú Web, Productos)
 * @param {50|60|70|80} [zIndex] - z-index del overlay, cada pantalla lo usaba distinto según qué otros modales convive
 * @param {number} [confirmDelaySeconds] - si se pasa (junto con onConfirm), el botón "Confirmar" arranca
 *   deshabilitado mostrando una cuenta regresiva y solo se puede apretar cuando llega a 0. Pensado para
 *   acciones destructivas de verdad (ej. borrar un canal con historial) donde un solo click -- aunque sea
 *   sobre un diálogo de confirmación -- no es fricción suficiente. No afecta ningún uso existente: por
 *   defecto es 0 (sin espera), igual que siempre.
 */
const CustomAlert = ({ type, title, message, onClose, onConfirm, compact = false, zIndex = 60, confirmDelaySeconds = 0 }) => {
  const zClass = Z_INDEX_CLASSES[zIndex] || Z_INDEX_CLASSES[60];
  const [secondsLeft, setSecondsLeft] = useState(confirmDelaySeconds);

  useEffect(() => {
    if (secondsLeft <= 0) return undefined;
    const timer = setTimeout(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [secondsLeft]);

  if (compact) {
    const cfg = COMPACT_CONFIGS[type] || COMPACT_CONFIGS.warning;
    const Icon = cfg.Icon;
    return (
      <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zClass} p-4`}>
        <div className={`${cfg.bg} border-2 ${cfg.border} rounded-xl shadow-2xl max-w-md w-full p-6`}>
          <div className="flex items-start gap-4">
            <Icon className={`w-8 h-8 ${cfg.iconColor} flex-shrink-0 mt-0.5`} />
            <div className="flex-1">
              <h3 className={`font-bold text-lg ${cfg.titleColor}`}>{title}</h3>
              {message && <p className="text-gray-700 mt-1 text-sm whitespace-pre-line">{message}</p>}
            </div>
          </div>
          <button onClick={onClose} className="mt-4 w-full py-2 px-4 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-800">
            Entendido
          </button>
        </div>
      </div>
    );
  }

  const config = FULL_CONFIGS[type] || FULL_CONFIGS.warning;
  const Icon = config.Icon;

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zClass} p-4`}>
      <div className={`${config.bgColor} border-2 ${config.borderColor} rounded-xl shadow-2xl max-w-md w-full p-6`}>
        <div className="flex items-start gap-4">
          <div className={`${config.iconColor} flex-shrink-0`}>
            <Icon className="w-12 h-12" />
          </div>
          <div className="flex-1">
            <h3 className={`text-xl font-bold ${config.titleColor} mb-2 flex items-center gap-2`}>
              <span>{config.emoji}</span>
              {title}
            </h3>
            <div className="text-gray-700 whitespace-pre-line text-sm">
              {message}
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          {onConfirm ? (
            <>
              <button
                onClick={onClose}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 font-bold rounded-lg hover:bg-gray-100 transition"
              >
                Cancelar
              </button>
              <button
                onClick={() => { if (secondsLeft > 0) return; onConfirm(); onClose(); }}
                disabled={secondsLeft > 0}
                className={
                  secondsLeft > 0
                    ? 'px-6 py-2 border-2 border-gray-300 text-gray-400 font-bold rounded-lg cursor-not-allowed'
                    : `px-6 py-2 ${config.borderColor} border-2 ${config.titleColor} font-bold rounded-lg hover:opacity-80 transition`
                }
              >
                {secondsLeft > 0 ? `Espere (${secondsLeft}s)` : 'Confirmar'}
              </button>
            </>
          ) : (
            <button
              onClick={onClose}
              className={`px-6 py-2 ${config.borderColor} border-2 ${config.titleColor} font-bold rounded-lg hover:opacity-80 transition`}
            >
              Entendido
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomAlert;
